import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { CoiListRow, ReferralRow } from "./useCois";
import type { SuggestionView } from "./useSuggestions";

export interface SentRow { id: string; title: string; content: string | null; companyId: string | null; companyName: string | null; at: string; byName: string | null }
export interface CoiDetailPayload extends CoiListRow {
  linkedinUrl: string | null; notes: string | null; firmBio: string | null; firmMetadata: any; specialtyNotes: string | null;
  lastTouchBy: string | null; mandatesFeeOpportunity: number | null;
  referrals: ReferralRow[]; sent: SentRow[]; suggestions: SuggestionView[];
}

/** One COI person with everything derived, plus the three writes the page makes. */
export function useCoiDetail(id: string | undefined) {
  const qc = useQueryClient();
  const query = useQuery<CoiDetailPayload>({
    queryKey: ["coi", id],
    queryFn: async () => {
      const r = await fetch(`/api/cois/${id}`, { credentials: "include" });
      if (!r.ok) throw Object.assign(new Error("Not found"), { status: r.status });
      return r.json();
    },
    enabled: !!id,
    // A 404 means "not a COI", which the page turns into a redirect; retrying it only delays that.
    retry: (n, e) => (e as { status?: number }).status !== 404 && n < 2,
  });
  const notFound = (query.error as { status?: number } | null)?.status === 404;
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["coi", id] });
    qc.invalidateQueries({ queryKey: ["cois"] });
    qc.invalidateQueries({ queryKey: ["coi-bd"] });
    qc.invalidateQueries({ queryKey: ["/api/dashboard/origination"] });
    qc.invalidateQueries({ queryKey: ["contact-activity-feed", id] });
    qc.invalidateQueries({ queryKey: ["company-activities"] });
  };
  const post = (path: string) => async (body: Record<string, unknown>) => {
    const r = await fetch(`/api/cois/${id}/${path}`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Failed");
  };
  const touch = useMutation({ mutationFn: post("touch"), onSuccess: invalidate });
  const referralSent = useMutation({ mutationFn: post("referral-sent"), onSuccess: invalidate });
  const update = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const r = await fetch(`/api/cois/${id}`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Update failed");
    },
    onSuccess: invalidate,
  });
  return { ...query, notFound, touch, referralSent, update };
}
